import type React from "react"

type AnnotationAction = "highlight" | "underline" | "box" | "circle"

interface HighlighterProps {
    children: React.ReactNode
    action?: AnnotationAction
    /** Hex colour, with or without an alpha channel */
    color?: string
}

/** Same colour, but at ~80% opacity, so thin strokes stay visible when the given colour is a soft tint. */
const stroke = (color: string) => {
    const hex = color.replace("#", "")
    return /^[0-9a-f]{6}([0-9a-f]{2})?$/i.test(hex) ? `#${hex.slice(0, 6)}cc` : color
}

/**
 * Marks a phrase the way a pen would: highlight, underline, box or circle.
 * Drawn with plain CSS on the text itself (no measured SVG overlay), so the marks always stay put when the
 * surrounding card scales, scrolls or reflows, and they wrap correctly across lines.
 */
export function Highlighter({ children, action = "highlight", color = "#ffd1dc" }: HighlighterProps) {
    const line = stroke(color)
    const style: React.CSSProperties = {
        boxDecorationBreak: "clone",
        WebkitBoxDecorationBreak: "clone",
    }

    if (action === "highlight") {
        Object.assign(style, { backgroundColor: color, borderRadius: "0.25em", padding: "0 0.2em" })
    } else if (action === "underline") {
        Object.assign(style, {
            backgroundImage: `linear-gradient(${line}, ${line})`,
            backgroundRepeat: "no-repeat",
            backgroundPosition: "0 100%",
            backgroundSize: "100% 0.14em",
            paddingBottom: "0.08em",
        })
    } else if (action === "circle") {
        Object.assign(style, { boxShadow: `inset 0 0 0 1.5px ${line}`, borderRadius: "1em", padding: "0.05em 0.5em" })
    } else {
        Object.assign(style, { boxShadow: `inset 0 0 0 1.5px ${line}`, borderRadius: "0.2em", padding: "0 0.25em" })
    }

    return <span style={style}>{children}</span>
}
